/**
 * Error Tracking Utility
 * Batches and sends errors to ClickHouse via API endpoint
 * Works in both client-side and server-side environments
 */

import { errorAlertDeduplicator } from './error-alert-deduplication';
import type { ErrorContext, TrackedError, ErrorSeverity as ErrorSeveritySchemaType } from './schemas/error-tracking';
import { errorTypeName } from './error-type-name';
import { errorDedupeKey, errorFingerprint } from './error-fingerprint';
import { getAnonymousId, getSessionId } from './tracking-context';

// ============================================================================
// Types and Enums
// ============================================================================

// Create ErrorSeverity enum from Zod type for backwards compatibility
export const ErrorSeverity = {
  INFO: 'info' as ErrorSeveritySchemaType,
  WARNING: 'warning' as ErrorSeveritySchemaType,
  ERROR: 'error' as ErrorSeveritySchemaType,
  CRITICAL: 'critical' as ErrorSeveritySchemaType,
} as const;

// Export severity type for consumers. The value/type name pairing is
// deliberate (enum-like const + its type, mirroring a TS enum) — values and
// types live in separate namespaces, so this is not a runtime redeclaration.
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ErrorSeverity = ErrorSeveritySchemaType;

/**
 * Severity for a failed HTTP response, split on whose fault the status is.
 *
 * A 4xx is the server telling the caller it asked for something it may not
 * have — a duplicate address, a stale id, a signed-out session. That is the
 * API working, and logging it at `error` puts a user's second click in the
 * same bucket as an outage. A 5xx is ours. The two exceptions are 408 and
 * 429: they are 4xx by number but they describe the server running out of
 * time or capacity, which is a system condition, not a caller mistake.
 */
export function severityForHttpStatus(status: number): ErrorSeverity {
  if (status === 408 || status === 429) return ErrorSeverity.ERROR;
  if (status >= 400 && status < 500) return ErrorSeverity.WARNING;
  return ErrorSeverity.ERROR;
}

// ============================================================================
// Configuration
// ============================================================================

// Batch configuration
const BATCH_SIZE = 5; // Smaller batch for errors
const BATCH_INTERVAL_MS = 10000; // Send batch every 10 seconds
const API_ENDPOINT = '/api/errors/log';

// String length limits
const MAX_STACK_LENGTH = 5000; // Limit stack trace size to prevent excessive storage
const MAX_MESSAGE_LENGTH = 1000; // Limit error message length

// Debouncing configuration
const ERROR_DEBOUNCE_MS = 5000; // Don't track same error within 5 seconds
const ERROR_DEBOUNCE_MAP_MAX_SIZE = 1000; // Maximum number of fingerprints to track in memory

// Hash calculation constants

// Critical errors are sent immediately, bypassing the queue
const CRITICAL_SEVERITIES: ErrorSeveritySchemaType[] = [ErrorSeverity.CRITICAL];

// ============================================================================
// Cross-realm errors
// ============================================================================

/** An Error as it looks from outside the realm that constructed it. */
interface ErrorLike {
  name?: unknown;
  message: string;
  stack: string;
}

/**
 * True for a value that is an Error in every way except `instanceof`.
 *
 * Both a string `message` and a string `stack` are required on purpose. That
 * pair is what an Error from another realm looks like, and it is narrow enough
 * that a rejected plain object — `{ message: 'nope' }` from an API body, say —
 * still takes the stringifying path and is not dressed up as a thrown Error.
 */
export function isErrorLike(reason: unknown): reason is ErrorLike {
  if (typeof reason !== 'object' || reason === null) return false;
  const candidate = reason as Record<string, unknown>;
  return typeof candidate.message === 'string' && typeof candidate.stack === 'string';
}

/**
 * A stable description of a rejection reason that is not an Error.
 *
 * The message carries only the KIND of thing -- its type, or its constructor and
 * key names -- so one kind is one cluster; the values go to `extra`, which is
 * context and not hashed. A constructor name of three characters or fewer is a
 * minified binding and reads as `Object`. For a reason of `undefined` (a bare
 * `reject()`), there is nothing to describe, so it records when on the page it
 * happened instead.
 */
export function describeNonErrorReason(reason: unknown): { message: string; extra: Record<string, unknown> } {
  const reasonType = reason === null ? 'null' : typeof reason;
  const extra: Record<string, unknown> = { reasonType };

  if (reason === null || typeof reason !== 'object') {
    if (reason !== undefined && reason !== null) extra.reasonValue = String(reason).slice(0, 200);
    // `performance` is a global in every browser and in Node 16+, server included.
    extra.msSinceLoad = Math.round(performance.now());
    if (typeof document !== 'undefined') {
      extra.readyState = document.readyState;
      extra.visibilityState = document.visibilityState;
    }
    return { message: `Unhandled rejection with a non-Error reason (${reasonType})`, extra };
  }

  const ctorName = (reason as { constructor?: { name?: unknown } }).constructor?.name;
  const ctor = typeof ctorName === 'string' && ctorName.length > 3 ? ctorName : 'Object';
  const keys = Object.keys(reason).sort();
  extra.reasonConstructor = ctor;
  extra.reasonKeys = keys.slice(0, 20);
  try {
    extra.reasonJson = JSON.stringify(reason)?.slice(0, 300);
  } catch {
    extra.reasonJson = '(not serializable)';
  }

  const message = (reason as { message?: unknown }).message;
  if (typeof message === 'string' && message) {
    return { message: `Unhandled rejection (${ctor}): ${message}`, extra };
  }
  return {
    message: `Unhandled rejection with a non-Error reason (${ctor} {${keys.slice(0, 5).join(', ')}})`,
    extra,
  };
}

/**
 * Which deployment a browser hostname belongs to. `dev.` and `new.` are the
 * staging hosts; localhost (including the Capacitor webview) is development.
 */
export function environmentForHostname(hostname: string): 'development' | 'staging' | 'production' {
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) return 'development';
  if (hostname.includes('dev.') || hostname.includes('new.')) return 'staging';
  return 'production';
}

/** Rebuild a cross-realm Error in this realm, keeping the evidence it carries. */
export function adoptCrossRealmError(reason: ErrorLike): Error {
  const error = new Error(reason.message);
  if (typeof reason.name === 'string' && reason.name) {
    error.name = reason.name;
  }
  error.stack = reason.stack;
  return error;
}

// ============================================================================
// Error Tracker Class
// ============================================================================

class ErrorTracker {
  private errorQueue: TrackedError[] = [];
  private batchTimer: NodeJS.Timeout | null = null;
  private globalContext: Partial<ErrorContext> = {};
  private isServer: boolean;
  private userId?: number;
  private recentErrors: Map<string, number> = new Map(); // fingerprint -> timestamp
  /**
   * Rows still waiting in the queue, by dedupe key. A repeat inside the debounce
   * window is still dropped, but first it bumps `repeats` on this row, so the row
   * that is sent says how often it happened. Before this, six photos failing in
   * one batch reached error_logs as one row, and triage held it back as a singleton.
   */
  private queuedByKey: Map<string, TrackedError> = new Map();

  constructor() {
    this.isServer = typeof window === 'undefined';

    // Send any queued errors before page unload (client-side only)
    if (!this.isServer) {
      // Not beforeunload alone: a headless close, an iOS tab switch and a
      // bfcache navigation skip it, and the queued batch (10s window) went
      // with the page. A bot whose Stripe card form failed 6s before it left
      // reached funnel_events (track-abandonment listens on pagehide) but
      // never error_logs. pagehide and a hidden tab are the last reliable
      // moments; flush is a no-op on an empty queue.
      const flushOnLeave = () => { void this.flush(); };
      window.addEventListener('beforeunload', flushOnLeave);
      window.addEventListener('pagehide', flushOnLeave);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flushOnLeave();
      });

      // Clear old session alerts on initialization
      errorAlertDeduplicator.clearIfSessionChanged();

      // Capture unhandled errors
      window.addEventListener('error', (event) => {
        // Skip Google Ads errors early (before creating Error object)
        if (event.filename && this.isGoogleAdsError(event.message, '', event.filename)) {
          return;
        }

        // Skip "Script error." early - cross-origin errors with no useful info
        if (event.message === 'Script error.' || event.message === 'Script error') {
          return;
        }

        // Skip errors with no filename (eval'd code from bots/extensions)
        if (!event.filename) {
          return;
        }

        // Skip errors not from sellwild.com scripts early
        if (!this.isFromOwnScripts(event.filename)) {
          return;
        }

        // Filter out browser-internal and extension / native bridge errors.
        // The `unhandledrejection` handler below has always done this; this
        // handler never did, so the same benign notification was dropped when
        // it arrived as a rejection and written to error_logs when it arrived
        // as an error event. "ResizeObserver loop completed with undelivered
        // notifications" is the standing example: the browser reports it with
        // `event.filename` set to the document URL, so isFromOwnScripts waves
        // it through and it files as one of ours.
        if (this.isBrowserExtensionError(event.message, event.error?.stack)) {
          return;
        }

        // Properly extract error - handle edge cases where event.message might not be a string
        let error: Error;
        if (event.error instanceof Error) {
          error = event.error;
        } else if (typeof event.message === 'string' && event.message) {
          error = new Error(event.message);
        } else {
          // Fallback for edge cases (e.g., Safari/iOS where message can be an Event object)
          // filename is set: an event without one returned above.
          error = new Error(`Error at ${event.filename}:${event.lineno || 0}:${event.colno || 0}`);
        }

        this.captureException(error, {
          component: 'window.onerror',
          extra: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        });
      });

      // Capture unhandled promise rejections
      window.addEventListener('unhandledrejection', (event) => {
        let error: Error;
        let rejectionExtra: Record<string, unknown> | undefined;
        if (event.reason instanceof Error) {
          error = event.reason;
        } else if (event.reason instanceof Event) {
          // Script load failures produce Event objects as rejection reasons
          const target = event.reason.target as HTMLScriptElement | null;
          const failedSrc = target?.src || target?.id || event.reason.type || 'unknown';
          // Skip third-party script load failures (ad SDKs, analytics, etc.)
          // The stack trace points to our code (which initiated the load), but the
          // failed resource is external — so isFromOwnScripts won't catch it.
          if (failedSrc !== 'unknown' && !this.isFromOwnScripts(failedSrc)) {
            return;
          }
          error = new Error(`Script load failed: ${failedSrc}`);
        } else if (typeof event.reason === 'string') {
          error = new Error(event.reason);
        } else if (isErrorLike(event.reason)) {
          // Safari rejects with an Error built in another realm (an iframe, a
          // worker, an injected script), and a cross-realm Error fails
          // `instanceof Error` against our realm's constructor. Stringifying it
          // here would throw the original stack away and file this line as the
          // throw site, which is exactly how cluster c44e4a1c1f66d958 became
          // undiagnosable. Carry the name, message and stack across instead.
          error = adoptCrossRealmError(event.reason);
        } else {
          // Not an Error at all. String() made a row whose whole message was
          // "undefined" or "[object Object]" (e494a8a7174a382f, 11 rows nobody
          // could read). Name the kind of thing instead, and put its shape in
          // context, where it does not split the fingerprint.
          const described = describeNonErrorReason(event.reason);
          error = new Error(described.message);
          error.name = 'NonErrorRejection';
          rejectionExtra = described.extra;
        }

        // Next.js router fires this invariant when static props fail and
        // it tries to hard-navigate to the URL the user is already on.
        // Our error boundary already handles recovery via the API fallback,
        // so this rejection is harmless noise.
        if (error.message?.includes('Invariant: attempted to hard navigate to the same URL')) {
          return;
        }

        // The unsaved-changes guard cancels a navigation by throwing out of
        // `routeChangeStart` (hooks/useUnsavedChangesWarning.ts — Next.js has
        // no cancel API), and that throw escapes the router as a rejection.
        // It only happens when the user answered the confirm with "stay", so
        // it is a deliberate success path, not a fault.
        if (error.message === 'Route change aborted') {
          return;
        }

        // Filter out browser extension / native bridge errors
        // These come from Chrome extensions, password managers, antivirus, etc.
        if (this.isBrowserExtensionError(error.message, error.stack)) {
          return;
        }

        // Skip errors not from our scripts (same check as window.onerror)
        const stackInfo = this.parseStackTrace(error.stack);
        if (stackInfo.file && !this.isFromOwnScripts(stackInfo.file, error.stack)) {
          return;
        }

        this.captureException(error, {
          component: 'unhandledrejection',
          ...(rejectionExtra ? { extra: rejectionExtra } : {}),
        });
      });
    }
  }

  /**
   * The session this error belongs to -- the SAME one `events` and
   * `funnel_events` are written under.
   *
   * This class used to mint its own into `sw_error_session_id`, which is why
   * "which errors did the people who abandoned checkout hit?" returned nothing
   * on production rather than an answer: 886 error sessions, 0 of them matching
   * a funnel or analytics session. See the header of `utils/tracking-context.ts`.
   *
   * Read per error rather than cached on the instance. The old code took its id
   * once at module import and held it for the life of the tab, so a session that
   * rolled over after 30 idle minutes left this tracker filing errors under an
   * id nothing else used any more.
   */
  private currentSessionId(): string {
    if (this.isServer) return '';
    return getSessionId();
  }

  /** The browser this error came from, on the shared key. */
  private currentAnonymousId(): string {
    if (this.isServer) return '';
    return getAnonymousId();
  }

  /**
   * Get environment name
   */
  private getEnvironment(): string {
    if (this.isServer) {
      return process.env.NODE_ENV || 'development';
    }

    // isServer is `typeof window === 'undefined'`, so window exists here.
    return environmentForHostname(window.location.hostname);
  }

  /**
   * Get Vercel deployment ID for source map matching
   * This is embedded at build time via next.config.js
   */
  private getDeploymentId(): string | undefined {
    // Access via process.env which is replaced at build time
    const deploymentId = process.env.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID;
    return deploymentId || undefined;
  }

  /**
   * Parse user agent to extract browser/OS info (client-side only)
   */
  private parseUserAgent(): { browser?: string; os?: string; platform?: string } {
    if (this.isServer || typeof window === 'undefined' || typeof navigator === 'undefined') return {};

    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';

    // Detect browser
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';

    // Detect OS
    if (ua.includes('Win')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS')) os = 'iOS';

    return {
      browser,
      os,
      platform: navigator.platform,
    };
  }

  /**
   * Generate error fingerprint for deduplication.
   *
   * Shares utils/error-fingerprint with the server loggers, so the same defect
   * reported from the browser and from an API route lands under one identity, and
   * a client cluster survives the chunk hash changing on the next deploy.
   */
  private generateFingerprint(
    errorType: string,
    message: string,
    file?: string,
    line?: number
  ): string {
    return errorFingerprint({ errorType, message, file, line });
  }

  /**
   * Parse error stack trace to extract file/line/column
   */
  private parseStackTrace(stack?: string): { file?: string; line?: number; column?: number } {
    if (!stack) return {};

    // Formats: "at fn (file:line:column)", "at file:line:column", "fn@file:line:column".
    // The file group stops at whitespace and parens rather than at the first colon,
    // so a URL keeps its scheme. Chopping "https:" off left "//cdn.example.com/x.js",
    // which isFromOwnScripts read as one of our own root-relative paths.
    const FRAME = /([^\s()]+):(\d+):(\d+)/;
    const lines = stack.split('\n');
    const isFrame = (l: string) => /^\s*at\s/.test(l) || l.includes('@');
    const match = lines.filter(isFrame).map(l => l.match(FRAME)).find(Boolean) ||
      lines.map(l => l.match(FRAME)).find(Boolean);
    if (!match) return {};

    let file = match[1];
    // Firefox prefixes the frame with the function name: "fn@https://host/x.js"
    const at = file.lastIndexOf('@');
    if (at !== -1 && /^(?:[a-z][a-z0-9+.-]*:|\/|\.)/i.test(file.slice(at + 1))) {
      file = file.slice(at + 1);
    }

    return {
      file,
      line: parseInt(match[2]),
      column: parseInt(match[3]),
    };
  }

  /**
   * Truncate string to max length
   */
  private truncate(str: string, maxLength: number): string {
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength - 3) + '...';
  }

  /**
   * Check if error should be debounced (skip tracking)
   * Returns true if the same error was recently tracked.
   *
   * Keyed on the raw message and page rather than the fingerprint. The fingerprint
   * is deliberately coarse — it folds record ids and chunk hashes out so one bug is
   * one row on the dashboard — and debouncing on it would drop "Indexed listing 2"
   * because "Indexed listing 1" arrived a second earlier. Same reasoning as the
   * server-side window in utils/clickhouse/error-logging.ts.
   */
  /**
   * Count a debounced repeat on the row it duplicates, if that row has not been
   * sent yet. Once it has, there is nothing left to carry the count, and the
   * repeat is dropped as before.
   */
  private countRepeat(key: string): void {
    const queued = this.queuedByKey.get(key);
    if (!queued) return;
    const context = queued.additional_context ?? {};
    context.repeats = String((Number(context.repeats) || 0) + 1);
    queued.additional_context = context;
  }

  private shouldDebounce(key: string): boolean {
    const now = Date.now();
    const lastSeen = this.recentErrors.get(key);

    if (lastSeen && now - lastSeen < ERROR_DEBOUNCE_MS) {
      return true; // Skip this error, it was recently tracked
    }

    // Update or add the key with current timestamp
    this.recentErrors.set(key, now);

    // Cleanup old entries (keep map size manageable)
    if (this.recentErrors.size > ERROR_DEBOUNCE_MAP_MAX_SIZE) {
      const cutoff = now - ERROR_DEBOUNCE_MS;
      for (const [fp, ts] of this.recentErrors.entries()) {
        if (ts < cutoff) {
          this.recentErrors.delete(fp);
        }
      }
    }

    return false;
  }

  /**
   * Check if error should trigger an in-app alert
   * Returns true if error meets severity threshold and hasn't been alerted this session
   */
  private shouldTriggerAlert(severity: ErrorSeverity, fingerprint: string, trackedError?: TrackedError): boolean {
    // Only alert for Error and Critical severity
    if (severity !== ErrorSeverity.ERROR && severity !== ErrorSeverity.CRITICAL) {
      return false;
    }

    // Skip alerts for generic "Script error." from cross-origin scripts
    // These are tracked in ClickHouse but don't show user notifications
    if (trackedError?.message === 'Script error.') {
      return false;
    }

    // Skip alerts for common JavaScript syntax/type errors
    // These are programming errors, not user-facing errors
    // Still tracked in ClickHouse for debugging
    const skipErrorTypes = ['TypeError', 'ReferenceError', 'SyntaxError'];
    if (trackedError?.error_type && skipErrorTypes.includes(trackedError.error_type)) {
      return false;
    }

    // Check if already alerted in this session
    if (errorAlertDeduplicator.hasBeenAlerted(fingerprint)) {
      return false;
    }

    // Environment-specific logic
    const environment = this.getEnvironment();
    if (environment === 'development') {
      // In dev, only show CRITICAL to reduce noise
      return severity === ErrorSeverity.CRITICAL;
    }

    return true;
  }

  /**
   * Trigger in-app error alert via custom event
   * Dispatches 'error-alert' event for NotificationProvider to handle
   */
  private triggerErrorAlert(trackedError: TrackedError): void {
    if (this.isServer || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    try {
      // Mark as alerted in this session
      errorAlertDeduplicator.markAsAlerted(trackedError.fingerprint);

      // Dispatch custom event for NotificationProvider
      // Include error details for debugging
      // Full error details are still sent to ClickHouse
      const component = trackedError.additional_context?.component;
      const errorMessage = trackedError.message === 'Script error.'
        ? `Error in ${trackedError.file || 'unknown file'}${component ? ` (${component})` : ''}`
        : trackedError.message.length > 100
          ? trackedError.message.substring(0, 100) + '...'
          : trackedError.message;

      const event = new CustomEvent('error-alert', {
        detail: {
          msg: `${errorMessage} - We've been notified`,
          type: 'error' as const,
          actionURL: undefined,
          dismissed: false,
          metadata: {
            fingerprint: trackedError.fingerprint,
            severity: trackedError.severity,
            error_type: trackedError.error_type,
            file: trackedError.file,
            line: trackedError.line,
            component,
            page_url: trackedError.page_url,
          }
        }
      });

      document.dispatchEvent(event);
    } catch (_err) {
      // Silent failure - never break the app due to alerting
    }
  }

  /**
   * Set global error context (applies to all future errors)
   */
  setErrorContext(context: Partial<ErrorContext>): void {
    this.globalContext = { ...this.globalContext, ...context };
  }

  /**
   * Clear global error context
   */
  clearErrorContext(): void {
    this.globalContext = {};
  }

  /**
   * Get a copy of the current global error context
   */
  getErrorContext(): Partial<ErrorContext> {
    return { ...this.globalContext };
  }

  /**
   * Set user ID (call after login)
   */
  identify(userId: number): void {
    this.userId = userId;
    if (!this.isServer) {
      localStorage.setItem('sw_error_user_id', userId.toString());
    }
  }

  /**
   * Clear user ID (call after logout)
   */
  reset(): void {
    this.userId = undefined;
    if (!this.isServer) {
      // The session itself is rolled over by `analytics.reset()`, which owns the
      // one identity now. Rolling it here too would give the two trackers
      // different ids for the rest of the visit -- the drift this consolidation
      // removed.
      try {
        localStorage.removeItem('sw_error_user_id');
      } catch {
        // Blocked storage must not strand a logout.
      }
    }
  }

  /**
   * Whether this browser has a signed-in user on record, from this page or an
   * earlier one. Storage that throws answers false.
   */
  hasKnownUser(): boolean {
    try {
      return this.getUserId() !== undefined;
    } catch {
      return false;
    }
  }

  /**
   * Get stored user ID
   */
  private getUserId(): number | undefined {
    if (this.userId) return this.userId;
    if (this.isServer || typeof window === 'undefined') return undefined;
    const userId = localStorage.getItem('sw_error_user_id');
    return userId ? parseInt(userId) : undefined;
  }

  /**
   * Check if error originates from browser extensions or native bridge injections
   * (password managers, ad blockers, antivirus plugins, Android WebView bridges, etc.)
   */
  private isBrowserExtensionError(message: string, stack?: string): boolean {
    // Errors from browser-injected code that runs inside our bundle context.
    // These reference variables/APIs that don't exist in our source code.
    const browserInternalPatterns = [
      // Browser internals
      /ResizeObserver loop/i,                     // Browser resize observer (benign)
      /AbortError.*The operation was aborted/i,   // Browser navigation abort
      /NotAllowedError.*permissions/i,            // Browser permission prompts

      // Safari autofill / form internals
      /autofillFieldData/i,                       // Safari autofill field inspection
      /AutoFillPopover/i,                         // Safari autofill popover
      /formControlElement/i,                      // Safari form control inspection
      /_AutoFill/i,                               // Safari autofill prefix

      // Chrome autofill / password manager
      /gCrWeb/i,                                  // Chrome iOS WebView bridge
      /clearPredictionPhrase/i,                   // Chrome input prediction
      /shouldShow.*Prediction/i,                  // Chrome input prediction

      // WebView / native bridges
      /Object Not Found Matching Id:\d+/i,        // Chromium GinJavaBridge (extensions, WebView)
      /JavascriptInterface/i,                     // Android WebView bridge
      /WKWebView/i,                               // iOS WKWebView bridge

      // Extension-injected globals
      /Cannot read properties of null \(reading '__/i,
      /evaluating 'window\.__/i,
      /evaluating 'document\.__/i,
      /instantSearchSDKJSMiddleware/i,            // Algolia browser extension
      /kaspersky/i,                               // Kaspersky antivirus injection
      /norton/i,                                  // Norton antivirus injection

      // DuckDuckGo privacy browser injects feature-flag scripts into page context
      /feature named .* was not found/i,
    ];

    if (browserInternalPatterns.some(p => p.test(message))) {
      return true;
    }

    if (stack) {
      const extensionStackPatterns = [
        /chrome-extension:/i,
        /moz-extension:/i,
        /safari-extension:/i,
        /safari-web-extension:/i,
        // Safari 16+ never shows an extension's script URL; it prints every frame
        // as `webkit-masked-url://hidden/`. Without this, one signed-in Safari
        // user's extension filed 63 bare `reject()` rejections from a hidden
        // /account/notifications tab (2026-09-30..10-02) as ours, though our
        // source has no bare reject() at all.
        /webkit-masked-url:/i,
      ];
      if (extensionStackPatterns.some(p => p.test(stack))) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if error is from Google Ads scripts (should be ignored)
   */
  private isGoogleAdsError(message: string, stack?: string, file?: string): boolean {
    const googleAdsPatterns = [
      /googletagmanager/i,
      /googlesyndication/i,
      /google-analytics/i,
      /adsbygoogle/i,
      /gtag/i,
      /goog_/i,
      /_gac/i,
      /doubleclick\.net/i,
      /googleadservices/i,
      /TagError/i,
    ];

    // Check message
    if (googleAdsPatterns.some(pattern => pattern.test(message))) {
      return true;
    }

    // Check stack trace
    if (stack && googleAdsPatterns.some(pattern => pattern.test(stack))) {
      return true;
    }

    // Check file
    if (file && googleAdsPatterns.some(pattern => pattern.test(file))) {
      return true;
    }

    return false;
  }

  /**
   * Check if error originates from sellwild.com scripts
   * Returns true if error should be tracked, false to ignore
   */
  private isFromOwnScripts(file?: string, stack?: string): boolean {
    // Allow errors with no file info (might be our code)
    if (!file && !stack) return true;

    // Allowed domains for our scripts
    const allowedDomains = [
      'sellwild.com',
      'new.sellwild.com',
      'dev.sellwild.com',
      'localhost',
      '127.0.0.1',
    ];

    // Known third-party patterns to block (ads, analytics, chat widgets, extensions)
    const thirdPartyPatterns = [
      /facebook\.net/i,
      /fbevents/i,
      /connect\.facebook/i,
      /segment\.io/i,
      /cdn\.segment/i,
      /mixpanel/i,
      /amplitude/i,
      /hotjar/i,
      /intercom/i,
      /zendesk/i,
      /crisp\.chat/i,
      /tawk\.to/i,
      /drift/i,
      /livechat/i,
      /olark/i,
      /adsense/i,
      /pagead/i,
      /pubads/i,
      /chrome-extension:/i,
      /moz-extension:/i,
      /safari-extension:/i,
      /extensions\//i,
      /recaptcha/i,
      /gstatic\.com/i,
      /cloudflare/i,
      /cdn\.shopify/i,
      /pinterest/i,
      /twitter\.com\/i\/jot/i,
      /snap\.licdn/i,
      /bat\.bing/i,
      /clarity\.ms/i,
      /newrelic/i,
      /nr-data/i,
      /sentry\.io/i,
      /bugsnag/i,
      /cookielaw\.org/i,
      /onetrust/i,
      /optanon/i,
    ];

    // Check file path
    if (file) {
      // Block known third-party patterns
      if (thirdPartyPatterns.some(pattern => pattern.test(file))) {
        return false;
      }

      // "//host/path" is an absolute protocol-relative URL, not one of our paths.
      const isProtocolRelative = file.startsWith('//');

      if (!isProtocolRelative) {
        // Allow relative paths (our code) - includes webpack:// paths
        if (file.startsWith('/') || file.startsWith('./') || file.startsWith('webpack://')) {
          return true;
        }

        // Allow _next/static paths (our bundled code)
        if (file.includes('/_next/static/')) {
          return true;
        }
      }

      // Check if file URL matches allowed domains
      try {
        const url = new URL(isProtocolRelative ? `https:${file}` : file);
        if (allowedDomains.some(domain => url.hostname.includes(domain))) {
          return true;
        }
        // Not from our domains
        return false;
      } catch {
        // Not a valid URL, likely a relative path - allow it
        return true;
      }
    }

    // Only a stack is left: `file` returned above, and no file and no stack
    // returned at the top.
    // Block if stack contains only third-party patterns
    if (thirdPartyPatterns.some(pattern => pattern.test(stack))) {
      // But allow if our code is also in the stack
      const hasOurCode = allowedDomains.some(domain => stack.includes(domain)) ||
                        stack.includes('/_next/static/') ||
                        stack.includes('webpack://');
      if (!hasOurCode) {
        return false;
      }
    }

    // Extract URLs from stack and check if any are from external domains
    const urlPattern = /https?:\/\/[^\s)]+/g;
    const urls = stack.match(urlPattern) || [];

    if (urls.length > 0) {
      // Check if ALL URLs are external (no sellwild.com URLs)
      const hasOurUrl = urls.some(url => {
        try {
          const parsed = new URL(url);
          return allowedDomains.some(d => parsed.hostname.includes(d));
        } catch {
          return false;
        }
      });

      // If no sellwild.com URLs in stack, it's entirely third-party
      if (!hasOurUrl) {
        return false;
      }
    }

    return true; // Default: track it
  }

  /**
   * Main error tracking function
   */
  trackError(
    error: Error | string,
    severity: ErrorSeverity = ErrorSeverity.ERROR,
    context?: ErrorContext
  ): void {
    // Tolerate callers that pass a context object in place of severity —
    // the posted severity must be one of the enum strings or Zod rejects
    // the batch at /api/errors/log.
    if (severity !== null && typeof severity === 'object') {
      context = severity as unknown as ErrorContext;
      severity = ErrorSeverity.ERROR;
    }
    const isErrorObject = error instanceof Error;
    let rawMessage: string;
    if (isErrorObject) {
      rawMessage = error.message;
    } else if (typeof error === 'string') {
      rawMessage = error;
    } else if (typeof error === 'object' && error !== null) {
      // Serialize the full object so we can see everything in the error dashboard
      try {
        rawMessage = JSON.stringify(error, Object.getOwnPropertyNames(error), 0);
      } catch {
        rawMessage = String(error);
      }
    } else {
      rawMessage = String(error);
    }
    const stack = isErrorObject ? this.truncate(error.stack || '', MAX_STACK_LENGTH) : undefined;
    const errorType = errorTypeName(error, 'StringError');
    // A blank message is thrown away whole by /api/errors/log — validateErrorLog
    // requires a non-empty string, so the type, stack, page and context all go
    // with it and the only trace left is one "All errors filtered out" row that
    // cannot name the callsite. Keep the row alive under a stable placeholder.
    const message = rawMessage.trim()
      ? this.truncate(rawMessage, MAX_MESSAGE_LENGTH)
      : `Empty ${errorType} (no message)`;

    // Parse stack trace for location info
    const stackInfo = this.parseStackTrace(stack);

    // Skip Google Ads errors silently
    if (this.isGoogleAdsError(message, stack, stackInfo.file)) {
      return;
    }

    // Skip generic "Script error." from cross-origin scripts
    // These provide no useful info (no stack, no file) and are almost always third-party
    if (message === 'Script error.' || message === 'Script error') {
      return;
    }

    // Skip errors not from sellwild.com scripts (third-party ads, analytics, extensions)
    if (!this.isFromOwnScripts(stackInfo.file, stack)) {
      return;
    }

    // Generate fingerprint
    const fingerprint = this.generateFingerprint(
      errorType,
      message,
      stackInfo.file,
      stackInfo.line
    );

    // Check if error should be debounced (skip if the same occurrence was just tracked)
    const pageUrl =
      !this.isServer && typeof window !== 'undefined' ? window.location.href : undefined;
    const dedupeKey = errorDedupeKey({ fingerprint, message, pageUrl, file: stackInfo.file, line: stackInfo.line });
    if (this.shouldDebounce(dedupeKey)) {
      this.countRepeat(dedupeKey);
      return; // Skip tracking this duplicate error
    }

    // Get user agent info
    const uaInfo = this.parseUserAgent();

    // Flatten context for Map storage
    const flatContext: Record<string, string> = {};
    const mergedContext = { ...this.globalContext, ...context };
    Object.entries(mergedContext).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'object') {
          flatContext[key] = JSON.stringify(value);
        } else {
          flatContext[key] = String(value);
        }
      }
    });

    // Build tracked error
    const trackedError: TrackedError = {
      fingerprint,
      severity,
      message,
      stack_trace: stack,
      error_type: errorType,
      timestamp: new Date().toISOString(),
      environment: this.getEnvironment(),
      deployment_id: this.getDeploymentId(),
      session_id: this.currentSessionId() || undefined,
      anonymous_id: this.currentAnonymousId() || undefined,
      user_id: this.getUserId(),
      additional_context: Object.keys(flatContext).length > 0 ? flatContext : undefined,
      ...stackInfo,
      ...uaInfo,
    };

    // Add browser-specific context
    if (!this.isServer && typeof window !== 'undefined') {
      trackedError.page_url = pageUrl;
      trackedError.referrer = document.referrer || undefined;
      trackedError.user_agent = navigator.userAgent;
    }

    // NOTE: Error tracking ONLY sends to ClickHouse - never shows user notifications
    // User notifications come ONLY from explicit createNotification() calls in the code

    // Critical errors bypass the queue
    if (CRITICAL_SEVERITIES.includes(severity)) {
      void this.sendImmediately([trackedError]);
    } else {
      this.errorQueue.push(trackedError);
      this.queuedByKey.set(dedupeKey, trackedError);

      // Auto-flush if batch size reached
      if (this.errorQueue.length >= BATCH_SIZE) {
        void this.flush();
      } else {
        this.scheduleBatch();
      }
    }
  }

  /**
   * Capture an exception with context
   */
  captureException(error: Error, context?: ErrorContext): void {
    this.trackError(error, ErrorSeverity.ERROR, context);
  }

  /**
   * Capture a message with severity
   */
  captureMessage(message: string, severity: ErrorSeverity = ErrorSeverity.INFO, context?: ErrorContext): void {
    this.trackError(message, severity, context);
  }

  /**
   * Log error and track it (drop-in replacement for console.error)
   */
  logError(error: Error | string, context?: ErrorContext): void {
    console.error(error);
    this.trackError(error, ErrorSeverity.ERROR, context);
  }

  /**
   * Schedule batch send
   */
  private scheduleBatch(): void {
    if (this.batchTimer) return;

    this.batchTimer = setTimeout(() => {
      void this.flush();
    }, BATCH_INTERVAL_MS);
  }

  /**
   * Send errors immediately (for critical errors)
   */
  private async sendImmediately(errors: TrackedError[]): Promise<void> {
    // Skip sending on server-side (errors should be logged directly)
    if (this.isServer) {
      return;
    }

    try {
      // Use fetch for immediate send
      await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-SW-Error-Client': '1' },
        body: JSON.stringify({ errors }),
        keepalive: true,
      });
    } catch (error) {
      console.error('[ErrorTracker] Failed to send critical error:', error);
    }
  }

  /**
   * Flush all queued errors immediately
   */
  async flush(): Promise<void> {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.errorQueue.length === 0) return;

    // Skip sending on server-side (errors should be logged directly)
    if (this.isServer) {
      this.errorQueue = []; // Clear queue
      this.queuedByKey.clear();
      return;
    }

    const errorsToSend = [...this.errorQueue];
    this.errorQueue = [];
    this.queuedByKey.clear();

    try {
      // Use sendBeacon for reliability during page unload (client-side)
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify({ errors: errorsToSend })], {
          type: 'application/json',
        });
        navigator.sendBeacon(API_ENDPOINT, blob);
      } else {
        // Fallback to fetch
        await fetch(API_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-SW-Error-Client': '1' },
          body: JSON.stringify({ errors: errorsToSend }),
          keepalive: true,
        });
      }
    } catch (error) {
      console.error('[ErrorTracker] Failed to send errors:', error);
      // In development, don't silently swallow a failed send: re-surface the
      // actual error(s) that were being logged so the real bug is visible
      // instead of hidden behind a dead log endpoint. Gated on NODE_ENV (not
      // `getEnvironment()`, which reads the Capacitor webview hostname
      // `localhost` and so reports 'development' even in the prod TestFlight
      // build). NODE_ENV is 'production' for every `next build` — including the
      // Capacitor bundles — so this only ever throws under `next dev`.
      if (process.env.NODE_ENV === 'development') {
        const actual = new Error(
          errorsToSend.map((e) => `${e.error_type}: ${e.message}`).join('\n'),
        );
        // Point the throw at the real source, not this flush frame.
        if (errorsToSend[0]?.stack_trace) actual.stack = errorsToSend[0].stack_trace;
        throw actual;
      }
      // Don't re-queue failed errors to avoid infinite loops
    }
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

// Only create instance on client-side for browser features
// On server-side, create a basic version without browser-specific features
export const errorTracker = new ErrorTracker();

// ============================================================================
// Convenience Functions
// ============================================================================

/**
 * Track an error with severity and context
 */
export function trackError(
  error: Error | string,
  severity: ErrorSeverity = ErrorSeverity.ERROR,
  context?: ErrorContext
): void {
  errorTracker.trackError(error, severity, context);
}

/**
 * Capture an exception (shorthand for trackError with ERROR severity)
 */
export function captureException(error: Error, context?: ErrorContext): void {
  errorTracker.captureException(error, context);
}

/**
 * Capture a message with optional severity
 */
export function captureMessage(
  message: string,
  severity: ErrorSeverity = ErrorSeverity.INFO,
  context?: ErrorContext
): void {
  errorTracker.captureMessage(message, severity, context);
}

/**
 * Log error to console and track it (drop-in replacement for console.error)
 */
export function logError(error: Error | string, context?: ErrorContext): void {
  errorTracker.logError(error, context);
}

/**
 * Set global error context (applies to all future errors)
 */
export function setErrorContext(context: Partial<ErrorContext>): void {
  errorTracker.setErrorContext(context);
}

/**
 * Clear global error context
 */
export function clearErrorContext(): void {
  errorTracker.clearErrorContext();
}

/**
 * Set user ID for error tracking
 */
export function identifyUser(userId: number): void {
  errorTracker.identify(userId);
}

/**
 * Reset user tracking (e.g., after logout)
 */
export function resetUserTracking(): void {
  errorTracker.reset();
}

/**
 * Set funnel context for error tracking
 * This ensures all errors during a funnel have the session ID attached
 */
export function setFunnelContext(context: {
  funnel_type: 'checkout' | 'selling' | 'auth' | 'transaction' | 'account';
  session_id: string;
  step?: string;
}): void {
  const baseContext = {
    funnel_type: context.funnel_type,
    funnel_step: context.step,
  };

  // Map session_id to appropriate field based on funnel type
  let sessionField: Partial<ErrorContext> = {};
  switch (context.funnel_type) {
    case 'checkout':
      sessionField = { checkout_session_id: context.session_id };
      break;
    case 'selling':
      sessionField = { draft_session_id: context.session_id };
      break;
    case 'auth':
      sessionField = { flow_id: context.session_id };
      break;
    case 'transaction':
    case 'account':
      sessionField = { session_id: context.session_id };
      break;
  }

  errorTracker.setErrorContext({
    ...baseContext,
    ...sessionField,
  });
}

/**
 * Clear funnel context (call on unmount)
 */
export function clearFunnelContext(): void {
  errorTracker.clearErrorContext();
}

/**
 * Get current funnel context
 */
export function getFunnelContext(): Partial<ErrorContext> {
  return errorTracker.getErrorContext();
}

// Export types from schema (primary exports)
export type { TrackedError, ErrorContext } from './schemas/error-tracking';
